import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import FloatingButtons from "./components/ui/FloatingButtons";
import { PageLoader, ProtectedRoute } from "./components/ui/index";

import HomePage from "./pages/HomePage";
import { AboutPage, ServicesPage, ServiceDetailPage, ContactPage, PrivacyPage, TermsPage } from "./pages/InfoPages";
import { ProductsPage, ProductDetailPage } from "./pages/ProductPages";
import { BlogPage, BlogDetailPage } from "./pages/BlogPages";
import { GalleryPublicPage } from "./pages/GalleryPage";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminSEO from "./pages/admin/AdminSEO";
import AdminCertifications from "./pages/admin/AdminCertifications";
import { AdminServices, AdminMessages, AdminSettings, AdminCMS } from "./pages/admin/AdminSubPages";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return children;
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <FloatingButtons />
    </>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white pt-20 gap-4">
      <div className="font-heading text-8xl font-extrabold text-gray-100">404</div>
      <h1 className="font-heading text-2xl font-bold text-gray-900">Page Not Found</h1>
      <p className="text-gray-500">The page you are looking for does not exist.</p>
      <a href="/" className="btn-primary mt-2">Go Home</a>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SettingsProvider>
          <Router>
            <ScrollToTop />
            <PageLoader />
            <Routes>
              <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
              <Route path="/services/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
              <Route path="/products" element={<PublicLayout><ProductsPage /></PublicLayout>} />
              <Route path="/products/:slug" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogDetailPage /></PublicLayout>} />
              <Route path="/gallery" element={<PublicLayout><GalleryPublicPage /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
              <Route path="/privacy-policy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
              <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />

              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/*" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="brands" element={<AdminBrands />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="certifications" element={<AdminCertifications />} />
                <Route path="seo" element={<AdminSEO />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="cms" element={<AdminCMS />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
            </Routes>
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
